import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatDateLong } from '../../../utils/formatDate';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { formatCurrency } from '../../../utils/formatCurrency';
import { supabase } from '../../../lib/supabase';
import paymentRequestService from '../../../services/paymentRequestService';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useLanguage } from '../../../contexts/LanguageContext';
import { statusVariant } from '../../../utils/statusVariant';
import Badge, { BADGE_VARIANTS } from '../../../components/ui/Badge';
import PortalDropdown from '../../../components/ui/PortalDropdown';

// ─── Status config ─────────────────────────────────────────────────────────────
const STATUS_OPTIONS = ['draft', 'submitted', 'approved'];

/** Aujourd'hui, AAAA-MM-JJ, heure locale. */
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getStatusConfig = (t) => ({
  draft:     { label: t('paymentRequests.status.draft')     || 'Draft',     bg: 'bg-gray-100',  text: 'text-gray-600',  dot: 'bg-gray-400'  },
  submitted: { label: t('paymentRequests.status.submitted') || 'Submitted', bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500'  },
  approved:  { label: t('paymentRequests.status.approved')  || 'Approved',  bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
});

const getBillingStatusConfig = (t) => ({
  not_invoiced: { label: t('paymentRequests.billing.notInvoiced') || 'Non facturée' },
  invoiced:     { label: t('paymentRequests.billing.invoiced')    || 'Facturée' },
  paid:         { label: t('paymentRequests.billing.paid')        || 'Payée' },
});

// ─── Locale-aware date helpers ─────────────────────────────────────────────────
const formatDateLocale = (dateStr, locale) => {
  if (!dateStr) return null;
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' })?.format(new Date(dateStr));
  } catch {
    // Repli : le helper central, PAS un toLocaleDateString() nu — celui-ci
    // rendait « 9/2/2026 » des que Intl echouait.
    return formatDateLong(dateStr, locale);
  }
};

const formatDateTimeLocale = (dateStr, locale) => {
  if (!dateStr) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })?.format(new Date(dateStr));
  } catch {
    return new Date(dateStr)?.toLocaleString();
  }
};

// ─── StatusPill (same pattern as ChangeOrdersTable) ────────────────────────────
const StatusPill = ({ request, statusConfig, onStatusChange, options = STATUS_OPTIONS }) => {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const btnRef = useRef(null);
  const currentStatus = request?.status || 'draft';
  const cfg = statusConfig?.[currentStatus] || statusConfig?.draft;

  const handleOpen = () => {
    if (updating) return;
    setOpen((v) => !v);
  };

  const handleSelect = async (status) => {
    setOpen(false);
    if (status === currentStatus) return;
    setUpdating(true);
    try {
      await onStatusChange?.(request?.id, status);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="relative inline-block">
      <Badge
        as="button"
        ref={btnRef}
        variant={statusVariant('paymentRequest', currentStatus)}
        onClick={handleOpen}
        disabled={updating}
        className="cursor-pointer select-none transition-opacity hover:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed"
        title="Change status"
      >
        {cfg?.label}
        {updating
          ? <Icon name="Loader" size={10} className="animate-spin ml-0.5" />
          : <Icon name="ChevronDown" size={10} className="ml-0.5" />
        }
      </Badge>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        align="left"
        width={150}
        items={options?.map((status) => ({
          label: statusConfig?.[status]?.label,
          dot: BADGE_VARIANTS?.[statusVariant('paymentRequest', status)]?.dot,
          active: currentStatus === status,
          onClick: () => handleSelect(status),
        }))}
      />
    </div>
  );
};

// ─── BillingStatusPill ─────────────────────────────────────────────────────────
/**
 * Suivi de facturation d'une DP, a cote de son statut d'approbation.
 * Rien avant l'approbation ; non facturee → « Marquer facturée » ;
 * facturee → « Paiement reçu » ; payee : lecture seule.
 */
const BillingStatusPill = ({ request, billingStatus, billingConfig, onMarkInvoiced, onMarkPaid }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  if (request?.status !== 'approved') {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const cfg = billingConfig?.[billingStatus] || billingConfig?.not_invoiced;

  if (billingStatus === 'paid') {
    return (
      <Badge variant={statusVariant('billing', 'paid')} className="cursor-default select-none">
        {cfg?.label}
        <Icon name="Lock" size={9} className="ml-0.5 opacity-60" />
      </Badge>
    );
  }

  const next = billingStatus === 'invoiced'
    ? { status: 'paid', label: t('paymentRequests.billing.markPaid'), run: () => onMarkPaid?.(request) }
    : { status: 'invoiced', label: t('paymentRequests.billing.markInvoiced'), run: () => onMarkInvoiced?.(request) };

  return (
    <div className="relative inline-block">
      <Badge
        as="button"
        ref={btnRef}
        variant={statusVariant('billing', billingStatus)}
        onClick={() => setOpen((v) => !v)}
        className="cursor-pointer select-none transition-opacity hover:opacity-80"
      >
        {cfg?.label}
        <Icon name="ChevronDown" size={10} className="ml-0.5" />
      </Badge>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        align="left"
        width={170}
        items={[{
          label: next?.label,
          dot: BADGE_VARIANTS?.[statusVariant('billing', next?.status)]?.dot,
          onClick: () => { setOpen(false); next?.run(); },
        }]}
      />
    </div>
  );
};

// ─── InvoiceDialog ─────────────────────────────────────────────────────────────
/**
 * « Marquer facturée » d'une DP approuvee : numero de facture et date de
 * facturation (obligatoires, aujourd'hui par defaut). onConfirm({ invoiceNumber, invoiceDate }).
 */
const InvoiceDialog = ({ request, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const field = 'w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';

  const submit = async () => {
    const number = String(invoiceNumber)?.trim();
    if (!number) {
      setError(t('paymentRequests.invoiceDialog.numberRequired'));
      return;
    }
    if (!invoiceDate) {
      setError(t('paymentRequests.invoiceDialog.dateRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onConfirm?.({ invoiceNumber: number, invoiceDate });
    } catch (err) {
      setError(err?.message || t('paymentRequests.invoiceDialog.error'));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onMouseDown={() => !saving && onCancel?.()}>
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl" onMouseDown={(e) => e?.stopPropagation()}>
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-sm font-semibold text-[#2C3E50]">
            {t('paymentRequests.invoiceDialog.title', { number: request?.number })}
          </h3>
        </div>
        <div className="px-5 pb-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('paymentRequests.invoiceDialog.number')} <span className="text-red-500">*</span>
            </label>
            <input type="text" autoFocus className={field} value={invoiceNumber} disabled={saving} placeholder="FAC-001" onChange={(e) => setInvoiceNumber(e?.target?.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('paymentRequests.invoiceDialog.date')} <span className="text-red-500">*</span>
            </label>
            <input type="date" className={field} value={invoiceDate} disabled={saving} onChange={(e) => setInvoiceDate(e?.target?.value)} />
          </div>
          {error && <p className="text-[11px] text-red-600">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button type="button" disabled={saving} onClick={onCancel} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            {t('paymentRequests.invoiceDialog.cancel')}
          </button>
          <button type="button" disabled={saving} onClick={submit} className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-[#E85D1F] rounded-lg transition-colors disabled:opacity-50">
            {t('paymentRequests.invoiceDialog.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PaymentReceivedDialog ─────────────────────────────────────────────────────
/**
 * « Paiement reçu » d'une DP : date de reception (obligatoire, aujourd'hui par
 * defaut) et montant recu (facultatif). onConfirm({ paidAt, paidAmount }).
 */
const PaymentReceivedDialog = ({ request, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [paidAt, setPaidAt] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const field = 'w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';

  const submit = async () => {
    if (!paidAt) {
      setError(t('paymentRequests.paidDialog.dateRequired'));
      return;
    }
    const cleaned = String(amount)?.replace(/\s/g, '')?.replace(',', '.');
    const paidAmount = cleaned === '' ? null : Number(cleaned);
    if (paidAmount !== null && (!Number.isFinite(paidAmount) || paidAmount < 0)) {
      setError(t('paymentRequests.paidDialog.amountInvalid'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onConfirm?.({ paidAt, paidAmount });
    } catch (err) {
      setError(err?.message || t('paymentRequests.paidDialog.error'));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onMouseDown={() => !saving && onCancel?.()}>
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl" onMouseDown={(e) => e?.stopPropagation()}>
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-sm font-semibold text-[#2C3E50]">
            {t('paymentRequests.paidDialog.title', { number: request?.number })}
          </h3>
        </div>
        <div className="px-5 pb-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('paymentRequests.paidDialog.date')} <span className="text-red-500">*</span>
            </label>
            <input type="date" className={field} value={paidAt} disabled={saving} onChange={(e) => setPaidAt(e?.target?.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('paymentRequests.paidDialog.amount')}</label>
            <input type="text" inputMode="decimal" className={field} value={amount} disabled={saving} placeholder="0,00" onChange={(e) => setAmount(e?.target?.value)} />
          </div>
          {error && <p className="text-[11px] text-red-600">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button type="button" disabled={saving} onClick={onCancel} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            {t('paymentRequests.paidDialog.cancel')}
          </button>
          <button type="button" disabled={saving} onClick={submit} className="px-4 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50">
            {t('paymentRequests.paidDialog.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── DocumentIconButton ────────────────────────────────────────────────────────
const DocumentIconButton = ({ request, onClick, language }) => {
  const { t } = useTranslation();
  const [docCount, setDocCount] = useState(0);
  const [docNames, setDocNames] = useState([]);

  useEffect(() => {
    if (!request?.id) return;
    const storagePath = `payment_request/${request?.id}`;
    supabase?.storage
      ?.from('contracts-documents')
      ?.list(storagePath, { limit: 20, sortBy: { column: 'name', order: 'asc' } })
      ?.then(({ data }) => {
        const filtered = (data || [])?.filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder');
        setDocCount(filtered?.length);
        setDocNames(filtered?.map(f => f?.name));
      })
      ?.catch(() => {});
  }, [request?.id]);

  const hasDocuments = docCount > 0;
  const docLabel = language === 'fr'
    ? `${docCount} document${docCount !== 1 ? 's' : ''}`
    : `${docCount} document${docCount !== 1 ? 's' : ''}`;

  return (
    <div className="relative inline-flex">
      <button
        onClick={onClick}
        className="relative inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors group hover:bg-gray-100"
        aria-label={docLabel}
      >
        <Icon
          name="FileText"
          size={14}
          className={`transition-colors ${hasDocuments ? 'text-orange-500' : 'text-gray-400'} group-hover:text-gray-700`}
        />
        {docCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 text-[9px] rounded-full font-semibold bg-primary text-white leading-none flex items-center justify-center"
            style={{ minWidth: '14px', height: '14px', padding: '0 3px' }}
          >
            {docCount}
          </span>
        )}
      </button>
    </div>
  );
};

// ─── Main Table ────────────────────────────────────────────────────────────────
const PaymentRequestsTable = ({ requests: requestsAlias, requestsProp: requestsPropAlias, onEdit, onDelete, onViewDocuments, onViewRequest, onApproveRequest, onRefresh, loading, selectedIds, onToggleRow, onToggleAll }) => {
  const requestsProp = requestsAlias ?? requestsPropAlias;
  console.log('[PaymentRequestsTable] destructured requestsProp=', requestsProp, 'typeof=', typeof requestsProp);
  if (process.env.NODE_ENV !== 'production' && requestsProp === undefined) {
    console.warn('[PaymentRequestsTable] requests prop is undefined. Parent must pass `requests={...}` or `requestsProp={...}`.');
  }
  const { t } = useTranslation();
  const { language } = useLanguage();
  // Locale string for Intl: 'fr' → 'fr-CA', 'en' → 'en-CA'
  const locale = language === 'fr' ? 'fr-CA' : 'en-CA';

  const [statusOverrides, setStatusOverrides] = useState({});
  // DP pour laquelle la fenetre « Marquer facturée » est ouverte.
  const [invoiceFor, setInvoiceFor] = useState(null);
  // DP pour laquelle la fenetre « Paiement reçu » est ouverte.
  const [paidFor, setPaidFor] = useState(null);
  // Facturation enregistree dans cette session, par DP (avant le rechargement de la liste) :
  // { billing_status, invoice_number, invoice_date, paid_at }.
  const [billingOverrides, setBillingOverrides] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);

  const buttonRefs = useRef({});

  const requests = (requestsProp || [])?.map(r => {
    const withStatus = statusOverrides?.[r?.id] ? { ...r, status: statusOverrides?.[r?.id] } : r;
    return billingOverrides?.[r?.id] ? { ...withStatus, ...billingOverrides?.[r?.id] } : withStatus;
  });

  const STATUS_CONFIG = getStatusConfig(t);
  const BILLING_CONFIG = getBillingStatusConfig(t);

  const handleStatusChange = useCallback(async (requestId, newStatus) => {
    // Intercept submitted → approved: delegate to parent for PDF gate
    const req = (requestsProp || [])?.find(r => r?.id === requestId);
    const currentStatus = statusOverrides?.[requestId] || req?.status;
    // Safety net: never leave 'approved'
    if (currentStatus === 'approved') return;
    if (currentStatus === 'submitted' && newStatus === 'approved') {
      onApproveRequest?.(req);
      return;
    }

    setStatusOverrides(prev => ({ ...prev, [requestId]: newStatus }));
    try {
      await paymentRequestService?.updateStatus(requestId, newStatus);
    } catch (err) {
      setStatusOverrides(prev => {
        const next = { ...prev };
        delete next?.[requestId];
        return next;
      });
    }
  }, [requestsProp, statusOverrides, onApproveRequest]);

  const handleMarkInvoiced = (request) => setInvoiceFor(request);
  const handleMarkPaid = (request) => setPaidFor(request);

  const confirmInvoice = async ({ invoiceNumber, invoiceDate }) => {
    const id = invoiceFor?.id;
    await paymentRequestService?.updateBillingStatus(id, 'invoiced', { invoice_number: invoiceNumber, invoice_date: invoiceDate });
    setBillingOverrides(prev => ({ ...prev, [id]: { ...prev?.[id], billing_status: 'invoiced', invoice_number: invoiceNumber, invoice_date: invoiceDate } }));
    setInvoiceFor(null);
    onRefresh?.();
  };

  const confirmPaid = async ({ paidAt, paidAmount }) => {
    const id = paidFor?.id;
    await paymentRequestService?.updateBillingStatus(id, 'paid', { paid_at: paidAt, paid_amount: paidAmount });
    setBillingOverrides(prev => ({ ...prev, [id]: { ...prev?.[id], billing_status: 'paid', paid_at: paidAt } }));
    setPaidFor(null);
    onRefresh?.();
  };

  const toggleMenu = (id) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!requests?.length) {
    console.log('[PaymentRequestsTable] empty-state check. length=', requestsProp?.length, 'isArray=', Array.isArray(requestsProp));
    return (
      <div className="text-center py-12">
        <Icon name="Receipt" size={40} className="mx-auto mb-3 text-gray-200" />
        <p className="text-sm text-gray-400">{t('paymentRequests.empty')}</p>
      </div>
    );
  }

  return (
    <div>
      <table className="w-full">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            <th className="px-4 py-3 w-8">
              <Checkbox
                checked={selectedIds?.size === requests?.length && requests?.length > 0}
                indeterminate={selectedIds?.size > 0 && selectedIds?.size < requests?.length}
                onChange={() => onToggleAll?.()}
              />
            </th>
            <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">
              {t('paymentRequests.table.requestNumber')}
            </th>
            <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">
              {t('paymentRequests.table.date')}
            </th>
            <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">
              {t('paymentRequests.table.totalAmount')}
            </th>
            <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">
              {t('paymentRequests.table.modifiedBy')}
            </th>
            <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">
              {t('paymentRequests.table.lastModified')}
            </th>
            <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">
              {t('paymentRequests.table.status')}
            </th>
            <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">
              {t('paymentRequests.table.billing')}
            </th>
            <th className="text-right text-xs font-medium text-gray-500 px-4 py-3 pr-8">
              {t('paymentRequests.table.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {requests?.map((req) => {
            const billingStatus = req?.billing_status || 'not_invoiced';
            // Dates seules (AAAA-MM-JJ) : lues a midi pour qu'aucun fuseau ne les recule d'un jour.
            const dateOnly = (d) => (d ? formatDateLocale(`${String(d)?.slice(0, 10)}T12:00:00`, locale) : null);
            return (
            <tr
              key={req?.id}
              className="transition-colors"
            >
              {/* Checkbox */}
              <td className="px-4 py-3 w-8" onClick={(e) => e?.stopPropagation()}>
                <Checkbox
                  checked={selectedIds?.has(req?.id) || false}
                  onChange={() => onToggleRow?.(req?.id)}
                />
              </td>

              {/* Request Number — clickable link to open preview */}
              <td className="px-4 py-3">
                <button
                  type="button"
                  onClick={() => onViewRequest?.(req)}
                  className="group flex items-center gap-1 cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <span className="text-xs font-semibold text-gray-900 group-hover:text-orange-600 group-hover:underline underline-offset-2 transition-colors">
                    DP{req?.number}
                  </span>
                  <span className="text-xs text-gray-400 group-hover:text-orange-500 transition-colors leading-none">›</span>
                </button>
                <p className="text-[10px] text-gray-400 mt-0.5">{t('paymentRequests.table.rowSubtitle')}</p>
              </td>

              {/* Date — locale-aware, date only */}
              <td className="px-4 py-3">
                <span className="text-xs text-gray-500">
                  {formatDateLocale(req?.created_at, locale) || <span className="text-gray-400">—</span>}
                </span>
              </td>

              {/* Total Amount — right-aligned */}
              <td className="px-4 py-3 text-right">
                <span className="text-xs font-semibold text-gray-900 tabular-nums block">
                  {req?.totalAmount != null ? formatCurrency(req?.totalAmount) : '—'}
                </span>
                <span className="text-[10px] text-gray-400 tabular-nums block mt-0.5">
                  {req?.montantSansTaxes != null
                    ? `${t('paymentRequests.table.beforeTax') || 'Avant taxes'}: ${formatCurrency(req?.montantSansTaxes)}`
                    : ''}
                </span>
              </td>

              {/* Modified By */}
              <td className="px-4 py-3">
                {req?.updater_name ? (
                  <span className="text-xs text-gray-700">{req?.updater_name}</span>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
              </td>

              {/* Last Modified — locale-aware, date + time */}
              <td className="px-4 py-3">
                {req?.updated_at ? (
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatDateTimeLocale(req?.updated_at, locale)}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
              </td>

              {/* Status — interactive dropdown pill or locked pill for approved */}
              <td className="px-4 py-3">
                {req?.status === 'approved' ? (
                  <Badge variant={statusVariant('paymentRequest', 'approved')} className="cursor-default select-none">
                    {STATUS_CONFIG?.approved?.label}
                    <Icon name="Lock" size={9} className="ml-0.5 opacity-60" />
                  </Badge>
                ) : (
                  <StatusPill
                    request={req}
                    statusConfig={STATUS_CONFIG}
                    onStatusChange={handleStatusChange}
                  />
                )}
              </td>

              {/* Billing — suivi de facturation, actif seulement une fois la DP approuvee */}
              <td className="px-4 py-3">
                <BillingStatusPill
                  request={req}
                  billingStatus={billingStatus}
                  billingConfig={BILLING_CONFIG}
                  onMarkInvoiced={handleMarkInvoiced}
                  onMarkPaid={handleMarkPaid}
                />
                {req?.status === 'approved' && billingStatus === 'invoiced' && req?.invoice_number && (
                  <span className="block text-xs text-gray-500 mt-0.5 whitespace-nowrap">
                    {req?.invoice_number}{req?.invoice_date ? ` — ${dateOnly(req?.invoice_date)}` : ''}
                  </span>
                )}
                {req?.status === 'approved' && billingStatus === 'paid' && req?.paid_at && (
                  <span className="block text-xs text-gray-500 mt-0.5 whitespace-nowrap">
                    {t('paymentRequests.table.paidOn', { date: dateOnly(req?.paid_at) })}
                  </span>
                )}
              </td>

              {/* Actions — doc icon + badge + ⋮ menu */}
              <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                <div className="flex items-center justify-end gap-0.5">
                  <DocumentIconButton
                    request={req}
                    onClick={() => onViewDocuments?.(req)}
                    language={language}
                  />

                  <button
                    ref={(el) => { buttonRefs.current[req?.id] = el; }}
                    onClick={() => toggleMenu(req?.id)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    aria-label="More actions"
                  >
                    <Icon name="MoreVertical" size={15} />
                  </button>

                  <PortalDropdown
                    anchorRef={{ current: buttonRefs?.current?.[req?.id] }}
                    open={openMenuId === req?.id}
                    onClose={() => setOpenMenuId(null)}
                    width={180}
                    items={[
                      ...(req?.status !== 'approved'
                        ? [{
                            label: t('paymentRequests.actions.edit'),
                            icon: 'Pencil',
                            onClick: () => onEdit?.(req),
                          }]
                        : []),
                      {
                        label: t('paymentRequests.actions.delete'),
                        icon: 'Trash2',
                        variant: 'danger',
                        onClick: () => onDelete?.(req),
                      },
                    ]}
                  />
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      {invoiceFor && <InvoiceDialog request={invoiceFor} onConfirm={confirmInvoice} onCancel={() => setInvoiceFor(null)} />}
      {paidFor && <PaymentReceivedDialog request={paidFor} onConfirm={confirmPaid} onCancel={() => setPaidFor(null)} />}
    </div>
  );
};

export default PaymentRequestsTable;
